import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Book, Envelope, LockKey, Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function formatErr(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return detail?.msg || String(detail);
}

export const Login = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState('email'); // 'email' | 'google'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      setUser(data.user);
      toast.success('Welcome back to Kavyalekh!');
      navigate('/dashboard');
    } catch (err) {
      const msg = formatErr(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/8546110/pexels-photo-8546110.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-background/95" />
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-card border border-border rounded-lg p-10 shadow-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Book size={32} weight="duotone" className="text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl tracking-tight mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Kavyalekh
            </h1>
            <p className="text-muted-foreground text-sm">Welcome back — sign in to continue</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 p-1 bg-muted/50 rounded-full">
            <button
              type="button"
              onClick={() => setMode('email')}
              data-testid="login-tab-email"
              className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${
                mode === 'email' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Email &amp; Password
            </button>
            <button
              type="button"
              onClick={() => setMode('google')}
              data-testid="login-tab-google"
              className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${
                mode === 'google' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Google
            </button>
          </div>

          {mode === 'email' ? (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label>
                <div className="relative">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    placeholder="you@example.com"
                    className="w-full pl-9 pr-3 py-3 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-muted-foreground">Password</label>
                  <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPwd ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    placeholder="At least 8 characters"
                    className="w-full pl-9 pr-10 py-3 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="login-submit"
                className="w-full bg-primary text-primary-foreground rounded-full py-3 font-semibold transition-transform hover:scale-[1.02] hover:opacity-90 disabled:opacity-60"
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </button>

              <p className="text-center text-sm text-muted-foreground pt-2">
                Don&apos;t have an account?{' '}
                <Link to="/signup" data-testid="login-signup-link" className="text-primary font-medium hover:underline">
                  Create one
                </Link>
              </p>
            </form>
          ) : (
            <>
              <button
                onClick={handleGoogleLogin}
                data-testid="google-signin-button"
                className="w-full bg-primary text-primary-foreground rounded-full py-4 px-6 font-semibold transition-transform hover:scale-[1.02] hover:opacity-90 flex items-center justify-center gap-3"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.509h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4" />
                  <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853" />
                  <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z" fill="#FBBC05" />
                  <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335" />
                </svg>
                Sign in with Google
              </button>
              <p className="text-center text-sm text-muted-foreground mt-6">
                New here?{' '}
                <button onClick={() => setMode('email')} className="text-primary font-medium hover:underline">
                  Use email &amp; password
                </button>
              </p>
            </>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
